import React from "react";
import { columnsConfig } from "../FlotteTable/flotteColumns.config";
import { useAtom } from "jotai";
import { flotteListAtom, transformMovements } from "../../hooks/useFlotte";
import useStore from "../../../../../context/useStore";
import { DateTime } from "luxon";
import flotteService from "../../../../../services/flotteService";
import { toast } from "react-toastify";

const useFlotteDetail = ({ item }) => {
  const [state] = useStore();
  const user = state.auth.user;
  const [flotteList, setFlotteList] = useAtom(flotteListAtom);

  // Grouper les colonnes par catégorie
  const categorizedColumns = React.useMemo(() => {
    const groups = {};

    Object.entries(columnsConfig).forEach(([key, config]) => {
      // Ignorer la colonne actions pour l'affichage détaillé
      if (
        key === "actions" ||
        [
          "Mvt. Contrat",
          "Mvt. Prolongation",
          "Mvt. Fin réelle",
          "Mvt. Vente",
        ].includes(config.category)
      )
        return;

      const category = config.category || "Autres";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push({ key, ...config });
    });

    return groups;
  }, []);

  const onAddStatut = async ({ statut }) => {
    try {
      // Récupération des données mises à jour depuis l'API
      const updatedVehicleData = await flotteService.addStatut({
        statut,
        item,
      });

      // Application des transformations nécessaires (mouvements -> attributs séparés)
      const transformedVehicleData = transformMovements(updatedVehicleData);

      // Mise à jour de la liste de flotte avec les données fraîches de l'API
      const updatedFlotteList = [...flotteList];
      const flotteIndex = updatedFlotteList.findIndex(
        (flotte) => flotte.id === item.id,
      );

      if (flotteIndex !== -1) {
        // Remplacement complet de l'élément avec les données transformées
        updatedFlotteList[flotteIndex] = transformedVehicleData;
        setFlotteList(updatedFlotteList);
        console.log(
          "Véhicule mis à jour depuis l'API:",
          transformedVehicleData,
        );
        toast.success("Statut modifié avec succès");
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout du statut");
      console.error("Erreur lors de l'ajout du statut:", error);
    }
  };

  const onResetPosition = async () => {
    try {
      const updatedVehicleData = await flotteService.resetPosition({
        item,
      });

      const transformedVehicleData = transformMovements(updatedVehicleData);

      const updatedFlotteList = [...flotteList];
      const flotteIndex = updatedFlotteList.findIndex(
        (flotte) => flotte.id === item.id,
      );

      if (flotteIndex !== -1) {
        updatedFlotteList[flotteIndex] = transformedVehicleData;
        setFlotteList(updatedFlotteList);
        toast.success("Position réinitialisée avec succès");
      }
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation de la position");
      console.error(
        "Erreur lors de la réinitialisation de la position:",
        error,
      );
    }
  };

  const onAddPosition = async ({ position }) => {
    try {
      const updatedVehicleData = await flotteService.addPosition({
        position,
        item,
      });

      const transformedVehicleData = transformMovements(updatedVehicleData);

      const updatedFlotteList = [...flotteList];
      const flotteIndex = updatedFlotteList.findIndex(
        (flotte) => flotte.id === item.id,
      );

      if (flotteIndex !== -1) {
        updatedFlotteList[flotteIndex] = transformedVehicleData;
        setFlotteList(updatedFlotteList);
        toast.success("Position modifiée avec succès");
      }
    } catch (error) {
      toast.error("Erreur lors de la modification de la position");
      console.error("Erreur lors de la modification de la position:", error);
    }
  };

  const onModifierSecteur = async ({ secteur }) => {
    try {
      const updatedVehicleData = await flotteService.modifierSecteur({
        secteur,
        item,
      });

      const transformedVehicleData = transformMovements(updatedVehicleData);

      const updatedFlotteList = [...flotteList];
      const flotteIndex = updatedFlotteList.findIndex(
        (flotte) => flotte.id === item.id,
      );

      if (flotteIndex !== -1) {
        updatedFlotteList[flotteIndex] = transformedVehicleData;
        setFlotteList(updatedFlotteList);
        toast.success("Secteur modifié avec succès");
      }
    } catch (error) {
      toast.error("Erreur lors de la modification du secteur");
      console.error("Erreur lors de la modification du secteur:", error);
    }
  };

  const onAddLocalisation = async ({ localisation }) => {
    try {
      const updatedVehicleData = await flotteService.addLocalisation({
        localisation,
        item,
      });

      const transformedVehicleData = transformMovements(updatedVehicleData);

      const updatedFlotteList = [...flotteList];
      const flotteIndex = updatedFlotteList.findIndex(
        (flotte) => flotte.id === item.id,
      );

      if (flotteIndex !== -1) {
        updatedFlotteList[flotteIndex] = transformedVehicleData;
        setFlotteList(updatedFlotteList);
        toast.success("Localisation modifiée avec succès");
      }
    } catch (error) {
      toast.error("Erreur lors de la modification de la localisation");
      console.error(
        "Erreur lors de la modification de la localisation:",
        error,
      );
    }
  };

  const onAddCommentaire = async ({ commentaire }) => {
    try {
      await flotteService.addCommentaire({
        commentaire,
        item,
      });
      const date = DateTime.now().toISO();
      const utilisateur = user?.firstname + " " + user?.lastname;
      const updatedFlotteList = [...flotteList];
      const flotteIndex = updatedFlotteList.findIndex(
        (flotte) => flotte.id === item.id,
      );
      if (flotteIndex !== -1) {
        if (!updatedFlotteList[flotteIndex].commentaires) {
          updatedFlotteList[flotteIndex].commentaires = [];
        }
        updatedFlotteList[flotteIndex].commentaires = [
          ...updatedFlotteList[flotteIndex].commentaires,
          { commentaire, date, utilisateur },
        ];

        console.log("updatedFlotteList commentaires", updatedFlotteList);
        setFlotteList(updatedFlotteList);
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout du commentaire:", error);
      toast.error("Erreur lors de l'ajout du commentaire");
    }
  };

  return {
    categorizedColumns,
    onAddStatut,
    onAddCommentaire,
    onAddPosition,
    onResetPosition,
    onAddLocalisation,
    onModifierSecteur,
  };
};

export default useFlotteDetail;
